<template>
  <div class="flex flex-col w-full h-full overflow-y-auto bg-surface-50">
    <div v-if="isLoading" class="flex flex-col w-full h-full justify-center items-center">
      <loader class="self-center" />
    </div>
    <div v-else class="w-full px-3.5 pb-6 md:flex md:items-start md:gap-6">
      <!-- Form column -->
      <div class="flex flex-col w-full md:max-w-[560px] min-w-0">
        <!-- Live preview (mobile — inline on top) -->
        <div class="md:hidden mt-3">
          <landing-preview
            :color="landingSiteSettings.leadColor"
            :title="contentSettings.title"
            :subtitle="contentSettings.subTitle"
            :logoImage="selectedLogoImage"
            :address="contentSettings.location"
            :phone="contentSettings.phone"
            :categories="previewCategories"
          />
          <div class="flex items-center justify-center gap-1.5 mt-2 mb-1 text-[11px] text-surface-500">
            <i class="pi pi-sparkles text-xs text-primary" /> תצוגה חיה — מתעדכנת בזמן שאתם עורכים
          </div>
        </div>

        <settings-group-label>מיתוג</settings-group-label>
        <settings-card>
          <!-- Lead color -->
          <div class="py-3 border-b border-surface-100">
            <div class="text-[11px] font-semibold text-surface-500 mb-2">צבע ראשי</div>
            <div class="flex items-center gap-2.5 flex-wrap">
              <button
                v-for="color in brandColors"
                :key="color"
                type="button"
                class="w-[30px] h-[30px] rounded-full box-border border-2"
                :class="isSelectedColor(color) ? 'border-white' : 'border-transparent'"
                :style="{ background: color, boxShadow: isSelectedColor(color) ? `0 0 0 2px ${color}` : 'none' }"
                @click="landingSiteSettings.leadColor = color"
              />
              <!-- Custom color via the full picker -->
              <button
                type="button"
                class="w-[30px] h-[30px] rounded-full box-border border-2 flex items-center justify-center"
                :class="isCustomColorSelected ? 'border-white' : 'border-transparent'"
                :style="{
                  background: isCustomColorSelected
                    ? landingSiteSettings.leadColor
                    : 'conic-gradient(#ef4444, #f59e0b, #22c55e, #3b82f6, #a855f7, #ef4444)',
                  boxShadow: isCustomColorSelected ? `0 0 0 2px ${landingSiteSettings.leadColor}` : 'none'
                }"
                @click="openChangeColor('lead')"
              >
                <i class="pi pi-palette text-[11px] text-white drop-shadow" />
              </button>
            </div>
          </div>

          <!-- Logo -->
          <div class="flex items-start gap-3 py-3 border-b border-surface-100">
            <div class="w-24 flex-none">
              <image-upload
                key="logo-upload"
                :uploadFunction="onSelectLogo"
                :imageUrl="selectedLogoImage"
                origin="logo"
                @remove="onRemoveLogo"
              />
            </div>
            <div class="flex-1 pt-1">
              <div class="text-sm font-medium">לוגו</div>
              <div class="text-[10.5px] text-surface-500 mt-0.5">PNG / JPG · עד 5 מ"ב</div>
            </div>
          </div>

          <!-- Background image -->
          <div class="py-3">
            <div class="text-[11px] font-semibold text-surface-500 mb-2">תמונת רקע</div>
            <image-upload
              key="bg-upload"
              :uploadFunction="onSelectBg"
              :ratio="2/3"
              :imageUrl="selectedBgImage"
              origin="bg"
              @remove="onRemoveBg"
            />
          </div>
        </settings-card>

        <settings-group-label>תוכן</settings-group-label>
        <settings-card>
          <div class="py-3 border-b border-surface-100">
            <label class="block text-[11px] font-semibold text-surface-500 mb-1.5" for="title">כותרת ראשית</label>
            <input-text id="title" v-model="contentSettings.title" type="text" class="w-full" />
          </div>
          <div class="py-3 border-b border-surface-100">
            <label class="block text-[11px] font-semibold text-surface-500 mb-1.5" for="subTitle">כותרת משנית</label>
            <input-text id="subTitle" v-model="contentSettings.subTitle" class="w-full" />
          </div>
          <div class="py-3">
            <label class="block text-[11px] font-semibold text-surface-500 mb-1.5" for="description">תיאור בית העסק</label>
            <Textarea
              id="description"
              v-model="contentSettings.about"
              :autoResize="false"
              cols="30"
              class="flex-none w-full"
              rows="3"
            />
          </div>
        </settings-card>

        <settings-group-label>גלריית תמונות</settings-group-label>
        <settings-card>
          <div class="py-3">
            <div class="text-[11px] text-surface-500 mb-2.5 leading-normal">
              תמונות של העסק והעבודות שלכם — יוצגו ללקוחות בעמוד ההזמנה
            </div>
            <gallery-grid v-model="selectedGallery" />
          </div>
        </settings-card>

        <settings-group-label>פרטי קשר</settings-group-label>
        <settings-card>
          <div class="py-3 border-b border-surface-100">
            <label class="block text-[11px] font-semibold text-surface-500 mb-1.5" for="address">כתובת בית העסק</label>
            <IconField class="w-full">
              <input-text id="address" v-model="contentSettings.location" class="w-full" type="text" />
              <InputIcon class="pi pi-map-marker" />
            </IconField>
          </div>
          <div class="py-3">
            <label class="block text-[11px] font-semibold text-surface-500 mb-1.5" for="phone">טלפון של בית העסק</label>
            <IconField class="w-full">
              <InputMask
                id="phone"
                v-model="contentSettings.phone"
                type="tel"
                inputmode="numeric"
                mask="999-999-9999"
                pattern="[0-9]*"
                class="w-full"
              />
              <InputIcon class="pi pi-phone" />
            </IconField>
          </div>
        </settings-card>

        <settings-group-label>רשתות חברתיות</settings-group-label>
        <settings-card>
          <div class="py-3 border-b border-surface-100">
            <label class="block text-[11px] font-semibold text-surface-500 mb-1.5" for="facebook">עמוד פייסבוק</label>
            <IconField class="w-full">
              <input-text id="facebook" v-model="contentSettings.facebook" style="direction: ltr" class="w-full" placeholder="facebook.com/..." />
              <InputIcon class="pi pi-facebook" />
            </IconField>
          </div>
          <div class="py-3 border-b border-surface-100">
            <label class="block text-[11px] font-semibold text-surface-500 mb-1.5" for="instagram">עמוד אינסטגרם</label>
            <IconField class="w-full">
              <input-text id="instagram" v-model="contentSettings.instagram" style="direction: ltr" class="w-full" placeholder="@" />
              <InputIcon class="pi pi-instagram" />
            </IconField>
          </div>
          <div class="py-3">
            <label class="block text-[11px] font-semibold text-surface-500 mb-1.5" for="whatsapp">קישור לווטסאפ</label>
            <IconField class="w-full">
              <input-text id="whatsapp" v-model="contentSettings.whatsapp" style="direction: ltr" class="w-full" placeholder="מספר טלפון או קישור לווטסאפ" />
              <InputIcon class="pi pi-whatsapp" />
            </IconField>
            <button
              type="button"
              class="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-primary bg-primary-50 border border-primary-100 rounded-full px-3 py-1.5 active:bg-primary-100 transition-colors"
              @click="useBusinessNumberForWhatsApp"
            >
              <i class="pi pi-phone !text-[11px]" /> השתמש במספר שלי
            </button>
          </div>
        </settings-card>

        <settings-group-label>פרטיות</settings-group-label>
        <settings-card>
          <settings-toggle-row
            title="נעילת האתר בסיסמא"
            description="רק לקוחות עם הסיסמא יוכלו לראות את האתר ולקבוע תור"
            :status="contentSettings.siteMode === 'locked'"
            last
            @value-changed="onSiteModeChange"
          >
            <input-text
              id="sitePassword"
              v-model="contentSettings.sitePassword"
              class="w-full"
              placeholder="מה הסיסמא לאתר?"
              type="text"
            />
          </settings-toggle-row>
        </settings-card>

        <settings-save-bar v-if="canEdit" :dirty="dirty" :saved="saved" :loading="loadingCta" @save="saveChanges" />
      </div>

      <!-- Live preview (desktop — sticky side column) -->
      <div class="hidden md:block md:w-[300px] md:flex-none md:sticky md:top-4 mt-3">
        <landing-preview
          :color="landingSiteSettings.leadColor"
          :title="contentSettings.title"
          :subtitle="contentSettings.subTitle"
          :logoImage="selectedLogoImage"
          :address="contentSettings.location"
          :phone="contentSettings.phone"
          :categories="previewCategories"
        />
        <div class="flex items-center justify-center gap-1.5 mt-2 text-[11px] text-surface-500">
          <i class="pi pi-sparkles text-xs text-primary" /> תצוגה חיה — מתעדכנת בזמן שאתם עורכים
        </div>
      </div>
    </div>
  </div>

  <Dialog
    v-model:visible="isSelectColorPopupVisible"
    modal
    header="בחירת צבע">
    <div class="flex justify-center items-center gap-2 p-4 pt-0 flex-col">
      <color-picker
        v-model:pureColor="tempColor"
        :isWidget="true"
        format="hex"
        useType="pure"
        pickerType="fk"
        :disableAlpha="true"
        :disableHistory="false"
        roundHistory
        lang="En"
        theme="white" />
      <Button type="button" label="שמירה" @click="updateColor"></Button>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { useAdminStore } from '@app/stores';
import { computed, onMounted, reactive, ref } from 'vue';
import {
  BASIC_TEMPLATE_CTA_COLOR_DEFAULT,
  BASIC_TEMPLATE_LEAD_COLOR_DEFAULT,
  ContractStatus,
  DEFAULT_STRUCTURE,
  IBusinessSiteDesign,
  IGalleryImage,
  ISocial,
  TSettingsContent,
  TSettingsLandingSite
} from '@torim-app/types';
import { SettingsApi } from '@app/api/admin';
import { useToast } from 'vue-toastification';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import { ColorPicker } from 'vue3-colorpicker';
import 'vue3-colorpicker/style.css';
import { AdminApi } from '@app/api/admin/admin-api';
import { trackEvent } from '@app/composables/posthog';
import LandingPreview from './LandingPreview.vue';
import GalleryGrid from './GalleryGrid.vue';
import SettingsCard from './ui/SettingsCard.vue';
import SettingsGroupLabel from './ui/SettingsGroupLabel.vue';
import SettingsToggleRow from './ui/SettingsToggleRow.vue';
import SettingsSaveBar from './ui/SettingsSaveBar.vue';
import { useSettingsForm } from './ui/settings-form';

const adminStore = useAdminStore();
const toast = useToast();

const brandColors = ['#8864ee', '#2563eb', '#0d9488', '#db2777', '#ea580c', '#18181b'];

const landingSiteSettings: TSettingsLandingSite = reactive<TSettingsLandingSite>(<TSettingsLandingSite>{});
const contentSettings: TSettingsContent = reactive<TSettingsContent>(<TSettingsContent>{});
const loadingCta = ref(false);
const selectedLogoImage = ref<string>();
const selectedBgImage = ref<string>();
const selectedGallery = ref<IGalleryImage[]>([]);

const isSelectColorPopupVisible = ref(false);
const tempColor = ref('');
const colorSelectionOrigin = ref<'lead' | 'cta'>();

const { dirty, saved, takeSnapshot, flashSaved } = useSettingsForm(() => ({ contentSettings, landingSiteSettings, gallery: selectedGallery.value }));

const isLoading = computed(() => adminStore.getFetchingInProgress);
const siteData = computed(() => adminStore.getBusinessSiteData);
const canEdit = computed(() => adminStore.getCanEdit);

// First few real service categories for the live preview chips
const previewCategories = computed(() => {
  const categories = [...new Set(adminStore.getServices.map(s => s.category).filter(Boolean))] as string[];
  return ['הכל', ...categories.slice(0, 2)];
});

const isSelectedColor = (color: string) => landingSiteSettings.leadColor?.toLowerCase() === color.toLowerCase();
const isCustomColorSelected = computed(() =>
  !!landingSiteSettings.leadColor && !brandColors.some(c => isSelectedColor(c))
);

const initData = () => {
  const { facebook, whatsapp, instagram } = siteData.value?.socials || <ISocial>{};
  const {
    leadColor,
    ctaColor,
    template,
    siteStructure = DEFAULT_STRUCTURE,
    logoImage,
    bgImage,
    gallery
  } = siteData.value?.design || <IBusinessSiteDesign>{};

  const contentDataToAssign: TSettingsContent = {
    siteMode: siteData.value?.configurations?.siteMode || 'open',
    sitePassword: siteData.value?.configurations?.sitePassword || '',
    title: siteData.value?.title,
    subTitle: siteData.value?.subTitle,
    location: siteData.value?.location,
    about: siteData.value?.about,
    phone: siteData.value?.phone?.replace(/\D/g, ''),
    ctaText: siteData.value?.configurations?.ctaText || 'קביעת תור',
    facebook, whatsapp, instagram
  };

  const designDataToAssign: TSettingsLandingSite = {
    leadColor: leadColor || BASIC_TEMPLATE_LEAD_COLOR_DEFAULT,
    ctaColor: ctaColor || BASIC_TEMPLATE_CTA_COLOR_DEFAULT,
    template: template,
    siteStructure: siteStructure.length ? siteStructure : DEFAULT_STRUCTURE
  };

  selectedLogoImage.value = logoImage;
  selectedBgImage.value = bgImage;
  selectedGallery.value = gallery ? gallery.map(g => ({ ...g })) : [];

  Object.assign(contentSettings, contentDataToAssign);
  Object.assign(landingSiteSettings, designDataToAssign);
  takeSnapshot();
};

const openChangeColor = (origin: 'lead' | 'cta') => {
  colorSelectionOrigin.value = origin;
  tempColor.value = origin === 'lead' ? landingSiteSettings.leadColor ?? '' : landingSiteSettings.ctaColor ?? '';
  isSelectColorPopupVisible.value = true;
};

const onSiteModeChange = (locked: boolean) => {
  contentSettings.siteMode = locked ? 'locked' : 'open';
};

const updateColor = () => {
  if (colorSelectionOrigin.value === 'lead')
    landingSiteSettings.leadColor = tempColor.value;
  else
    landingSiteSettings.ctaColor = tempColor.value;

  isSelectColorPopupVisible.value = false;
};

// Persist just the landing-site images immediately so the user doesn't have to
// press "save changes" a second time after cropping (mobile-first single action).
const persistImages = async () => {
  try {
    await SettingsApi.updateSettingsLandingSite({
      ...landingSiteSettings,
      logoImage: selectedLogoImage.value ?? '',
      bgImage: selectedBgImage.value ?? ''
    });
    toast.success('עדכנו את התמונה 😄');
    await adminStore.fetchBusinessData();
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לעדכן את התמונה');
  }
};

const onSelectLogo = (imageUrl: string) => {
  if (imageUrl) {
    selectedLogoImage.value = imageUrl;
    persistImages();
  }
};

const onSelectBg = (imageUrl: string) => {
  if (imageUrl) {
    selectedBgImage.value = imageUrl;
    persistImages();
  }
};

const onRemoveLogo = () => {
  selectedLogoImage.value = undefined;
  persistImages();
};

const onRemoveBg = () => {
  selectedBgImage.value = undefined;
  persistImages();
};

// Drop a leading protocol, an @, and leading slashes so we're left with the bare host/handle.
const stripPrefixes = (s: string) => s.replace(/^https?:\/\//i, '').replace(/^@+/, '').replace(/^\/+/, '');

// Accepts: full urls, m./www. hosts, fb.com/fb.me, "@page", "/page", or a bare page name.
const formatFacebookUrl = (input: string): string => {
  const raw = (input ?? '').trim();
  if (!raw) return '';
  const s = stripPrefixes(raw);
  if (/(^|\.)(facebook\.com|fb\.com|fb\.me)(\/|$)/i.test(s)) {
    const normalised = s
      .replace(/^m\.facebook\.com/i, 'www.facebook.com')
      .replace(/^facebook\.com/i, 'www.facebook.com')
      .replace(/^fb\.com/i, 'www.facebook.com')
      .replace(/\/+$/, '');
    return `https://${normalised}`;
  }
  // Treat as a page name / handle.
  return `https://www.facebook.com/${s.replace(/\/+$/, '')}`;
};

// Accepts: full urls, instagram.com/instagr.am hosts, "@user", or a bare username.
const formatInstagramUrl = (input: string): string => {
  const raw = (input ?? '').trim();
  if (!raw) return '';
  const s = stripPrefixes(raw);
  if (/(^|\.)(instagram\.com|instagr\.am)(\/|$)/i.test(s)) {
    const normalised = s
      .replace(/^instagram\.com/i, 'www.instagram.com')
      .replace(/^instagr\.am/i, 'www.instagram.com')
      .replace(/\/+$/, '');
    return `https://${normalised}`;
  }
  return `https://www.instagram.com/${s.replace(/\/+$/, '')}`;
};

// Accepts: an existing wa.me / whatsapp / api.whatsapp link, OR any phone number form
// (0..., 972..., +972..., 00972..., with spaces/dashes) → https://wa.me/<intl digits>.
const formatWhatsAppUrl = (input: string): string => {
  const raw = (input ?? '').trim();
  if (!raw) return '';
  if (/wa\.me|whatsapp\.com|api\.whatsapp/i.test(raw)) {
    return `https://${stripPrefixes(raw)}`;
  }
  let digits = raw.replace(/\D/g, '');
  if (!digits) return raw.startsWith('http') ? raw : `https://${stripPrefixes(raw)}`;
  if (digits.startsWith('00')) digits = digits.substring(2); // 00972... → 972...
  if (digits.length <= 10) {
    if (digits.startsWith('0')) digits = digits.substring(1); // local 0 → intl
    digits = `972${digits}`;
  }
  return `https://wa.me/${digits}`;
};

// "Use my number" — fill the WhatsApp field from the business phone as a wa.me link.
const useBusinessNumberForWhatsApp = () => {
  const phone = contentSettings.phone || siteData.value?.phone;
  if (!phone) {
    toast.warning('לא נמצא מספר טלפון לעסק');
    return;
  }
  contentSettings.whatsapp = formatWhatsAppUrl(String(phone));
};

const saveChanges = async () => {
  trackEvent('settings_content_saved');
  loadingCta.value = true;
  try {
    if (contentSettings.whatsapp) contentSettings.whatsapp = formatWhatsAppUrl(contentSettings.whatsapp);
    if (contentSettings.facebook) contentSettings.facebook = formatFacebookUrl(contentSettings.facebook);
    if (contentSettings.instagram) contentSettings.instagram = formatInstagramUrl(contentSettings.instagram);

    if (landingSiteSettings.leadColor)
      landingSiteSettings.leadColor = landingSiteSettings.leadColor.startsWith('#')
        ? landingSiteSettings.leadColor
        : '#' + landingSiteSettings.leadColor;

    if (landingSiteSettings.ctaColor)
      landingSiteSettings.ctaColor = landingSiteSettings.ctaColor.startsWith('#')
        ? landingSiteSettings.ctaColor
        : '#' + landingSiteSettings.ctaColor;

    landingSiteSettings.bgImage = selectedBgImage.value ?? '';
    landingSiteSettings.logoImage = selectedLogoImage.value ?? '';
    landingSiteSettings.gallery = selectedGallery.value;

    await Promise.all([
      SettingsApi.updateSettingsLandingSite(landingSiteSettings),
      SettingsApi.updateSettingsContent(contentSettings)
    ]);
    flashSaved();
    await adminStore.fetchBusinessData();
    initData();
  } catch (e) {
    toast.error('לא הצלחנו לעדכן את האתר שלך');
  }
  loadingCta.value = false;
};

onMounted(() => {
  initData();
  if (adminStore.getBusinessSiteData?.contract?.status === ContractStatus.Trial && !adminStore.getBusinessSiteData?.savedStates?.isEditedSettings)
    AdminApi.updateState({ isEditedSettings: true })
      .then(() => adminStore.fetchBusinessData())
      .catch(x => console.error(x));

});

defineExpose({ saveChanges, loadingCta, dirty });
</script>
